import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Typography, TextField, Box, FormControl, InputLabel, Select } from "@mui/material";
import { Search as IconSearch, AddCircle as IconAddCircle } from "@mui/icons-material";

import * as catalogoService from "../../services/catalogoService";
import { getsUsuarioView, getFilialesList } from "../../services/usuarioService";
import { obtenerUsuario } from "../../services/authService";
import DataGridBase from "../../components/common/DataGridBase";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Mantenimiento de usuarios del sistema: codigo, persona, clave, estado y los
// roles que tiene. Solo el ADMIN puede crear o modificar usuarios.
const CODIGO_ADMIN = "ADMIN";

const Index = () => {
    const { isReadOnly } = useMantenimiento("usuario");
    const navigate = useNavigate();

    const esAdmin = String(obtenerUsuario() || "").toUpperCase() === CODIGO_ADMIN;

    const [filters, setFilters] = useState({ nombres: "", idEstado: 0, idFilial: 0 });
    const [listEstado, setListEstado] = useState([]);
    const [listFilial, setListFilial] = useState([]);
    const [results, setResults] = useState([]);
    const [cargando, setCargando] = useState(true);

    const fetchResults = useCallback(async () => {
        setCargando(true);

        const data = await getsUsuarioView({
            nombres: filters.nombres == null ? "" : filters.nombres,
            idEstado: filters.idEstado == null ? 0 : filters.idEstado,
            idFilial: filters.idFilial == null ? 0 : filters.idFilial,
        }).catch((err) => { console.log("Err: ", err) });

        // El grid pide un id unico por fila: el usuario se identifica por su
        // codigo.
        //
        // La descripcion de la filial se resuelve al armar la fila. Se toma
        // primero la que trae la API y, si no viene, se busca el id en el
        // listado de filiales: resolverlo solo con el listado fallaba porque
        // el listado todavia no habia cargado cuando se pide el listado de
        // usuarios, y todo caia en "Todos".
        setResults((data || []).map((row) => {
            const id = row.idFilial || 0;
            const filial = (listFilial || []).find((f) => f.id == id);
            const descripcion = row.nombreFilial || (id > 0 && filial ? filial.descripcion : "");

            return {
                ...row,
                id: row.codigo,
                idFilial: id,
                filialDescripcion: descripcion || "Todos",
            };
        }));
        setCargando(false);
    }, [filters, listFilial])

    const fetchListEstado = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => { console.log("Err: ", err) });

        setListEstado(data || []);
    }, [])

    const fetchListFilial = useCallback(async () => {
        const data = await getFilialesList()
            .catch((err) => { console.log("Err: ", err) });

        setListFilial(data || []);
    }, [])

    useEffect(() => {
        fetchResults();
        fetchListEstado();
        fetchListFilial();
    }, [])

    const columns = [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'codigo', headerName: 'Codigo', width: 100, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate("/security/user/edit/" + encodeURIComponent(cellValues.row.codigo))}>
                    {cellValues.row.codigo}
                </Box>
            )
        },
        { field: 'nombres', headerName: 'Nombres', width: 220, sortable: false, headerAlign: 'left' },
        { field: 'roles', headerName: 'Roles', width: 200, sortable: false, headerAlign: 'left' },
        { field: 'filialDescripcion', headerName: 'Filial', width: 140, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fechaVencimientoClave', headerName: 'Vence Clave', width: 120, type: 'date'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estado', headerName: 'Estado', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Usuarios
                    </Typography>
                </Grid>
                <Grid container spacing={1} alignItems={"center"} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={8} sm={4}>
                        <TextField
                            name="nombres"
                            label="Código o nombres"
                            value={filters.nombres}
                            fullWidth
                            onChange={(event) => setFilters({ ...filters, nombres: event.target.value })}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    fetchResults();
                                }
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={8} sm={2}>
                        <FormControl fullWidth>
                            <InputLabel htmlFor="input-estadousuario" shrink>Estado</InputLabel>
                            <Select
                                native
                                label="Estado"
                                value={filters.idEstado}
                                onChange={(event) => setFilters({ ...filters, idEstado: parseInt(event.target.value) })}
                                inputProps={{ name: 'idEstado', id: 'input-estadousuario' }}
                                size="small"
                            >
                                <option key={0} value={0}>{"(Todos)"}</option>
                                {listEstado.map((row) => (
                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={8} sm={2}>
                        <FormControl fullWidth>
                            <InputLabel htmlFor="input-filial" shrink>Filial</InputLabel>
                            <Select
                                native
                                label="Filial"
                                value={filters.idFilial}
                                onChange={(event) => setFilters({ ...filters, idFilial: parseInt(event.target.value) })}
                                inputProps={{ name: 'idFilial', id: 'input-filial' }}
                                size="small"
                            >
                                <option key={0} value={0}>{"(Todos)"}</option>
                                {listFilial.map((row) => (
                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                        <Button variant="outlined" size="medium" fullWidth onClick={() => { fetchResults() }}>
                            <IconSearch />
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                toolbarButtons={esAdmin ? [
                                    {
                                        text: "Nuevo",
                                        icon: <IconAddCircle />,
                                        onClick: () => navigate("/security/user/edit/0")
                                    }
                                ] : []}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    )
}

export default Index;
